import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { DocumentMarketDataClassifications } from '@pages/ProductRegistration/LegalDocuments/DocumentMarketDataClassifications'
import {
  ReadDocumentMarketDataPage,
  useDocumentMarketDataIdState,
} from '@pages/ProductRegistration/LegalDocuments/ReadDocumentMarketDataPage'
import { isStocksOnlyLead } from '@pages/Signup/hooks/productValidationUtils'

import { useSessionEntity } from '@context/EntityContext'
import { usePublicDomainsReadContext } from '@context/PublicDomainsContext'
import { useSignup } from '@context/SignupContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'

import { isTickmillUKType } from '@utils/companyName.utils'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { LeadDto, isLeadTypeCorporate, isLeadTypeIndividual } from '@model/LeadDto'
import { TickmillProductType, isTickmillProductTypeETD } from '@model/TickmillProductType'

import { Loading } from '@shared/components/Loading/Loading'

import {
  AgreedLegalDocument,
  ReadDocumentPage,
} from '../../../Components/DocumentReadAndAgreeField'
import { FormValuesLegalDocs } from '../PersonalDetailsDocumentsFactory'
import { PersonalDetailsTemplate } from '../PersonalDetailsTemplate'
import { DocumentGroupsFactory } from './DocumentGroupsFactory'
import { DocumentItemFactory } from './DocumentItemFactory'

import styles from '../Generic.module.scss'

const PersonalDetailsStep6FormUI: React.FC<FormikProps<FormValuesLegalDocs> & OuterProps> = (
  props
) => {
  const { documents, isLoading, handleSubmit } = props

  useEffect(() => {
    if (!documents.length && !isLoading && !isStocksOnlyLead) {
      handleSubmit()
    }
  }, [documents.length, isLoading, handleSubmit])

  return (
    <Form className={styles.form} onSubmit={handleSubmit}>
      <FormDocumentsFactory {...props} />
    </Form>
  )
}

type FieldOuterProps = Omit<OuterProps, 'marketDataClassificationId'>

const FormDocumentsFactory: React.FC<FieldOuterProps> = (props) => {
  const entity = useSessionEntity()
  const { values, validateForm } = useFormikContext<FormValuesLegalDocs>()

  const readDocuments = values?.readDocuments
  const readDocumentMarketData = values?.readDocumentMarketData

  useEffect(() => {
    validateForm()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readDocuments])

  if (readDocuments && readDocuments.length > 0) {
    return <ReadDocumentPage />
  }

  if (readDocumentMarketData?.isDocumentMarketDataRead) {
    return <ReadDocumentMarketDataClientClassificationPage />
  }

  if (isTickmillUKType(entity)) {
    return <LegalDocumentsUK {...props} />
  }

  return <LegalDocuments {...props} />
}

const ReadDocumentMarketDataClientClassificationPage = () => {
  const { values, setFieldValue } = useFormikContext<FormValuesLegalDocs>()

  const { setMarketDataClassificationId } = useDocumentMarketDataIdState()

  const handleAccept = async () => {
    const value = values.readDocumentMarketData.marketDataClassificationId || 0
    setMarketDataClassificationId(value)
    setFieldValue('readDocumentMarketData', {
      marketDataClassificationId: value,
      isDocumentMarketDataRead: false,
      isDocumentMarketDataAccepted: true,
    })
  }

  return <ReadDocumentMarketDataPage onAccept={handleAccept} />
}

const LegalDocumentsUK: React.FC<FieldOuterProps> = (props) => {
  const { documents = [], groupReferences = [], products, isLoading } = props

  const { domains } = usePublicDomainsReadContext()

  const { t } = useTranslation()
  const legalDocumentsUrl = domains?.homePageUrl
    ? `${domains.homePageUrl}/about/legal-documents`
    : undefined

  return (
    <PersonalDetailsTemplate {...props} step={6} reversed>
      <div className='pb-4'>
        <Heading6 medium>{t('Sign up.Legal Documents')}</Heading6>
      </div>
      <div className='is-flex pt-4'>
        <Body1>
          {t('Sign up.Please refer to our legal documents section')}{' '}
          {legalDocumentsUrl ? (
            <Link title={t('Profile.Legal Documents')} href={legalDocumentsUrl}>
              {t('Sign up.here')}
            </Link>
          ) : (
            <span>{t('Sign up.here')}</span>
          )}
          {'.'}
        </Body1>
      </div>
      <div className='is-flex pt-4'>
        <Body1>
          {t('Sign up.Read & Accept the Legal Documents to continue with your registration.')}
        </Body1>
      </div>
      <Loading showLoadingIcon isLoading={isLoading}>
        <DocumentGroupsFactory
          groupReferences={groupReferences}
          documents={documents}
          renderItem={(props) => <DocumentItemFactory {...props} />}
        />
      </Loading>
      {isTickmillProductTypeETD(products) && <DocumentMarketDataClassifications />}
      <SubmitForm />
    </PersonalDetailsTemplate>
  )
}

const LegalDocuments: React.FC<FieldOuterProps> = (props) => {
  const { documents = [], groupReferences = [] } = props

  const { t } = useTranslation()

  return (
    <PersonalDetailsTemplate {...props} step={6} reversed>
      <div className='pb-4'>
        <Heading6 medium>{t('Sign up.Legal')}</Heading6>
      </div>
      <Body1>
        {t('Sign up.Please check the boxes below once you’ve reviewed our legal documentation.')}
      </Body1>
      <DocumentGroupsFactory
        groupReferences={groupReferences}
        documents={documents}
        renderItem={(props) => <DocumentItemFactory {...props} />}
      />
      <SubmitForm />
    </PersonalDetailsTemplate>
  )
}

const SubmitForm = () => {
  const { t } = useTranslation()

  const { isSubmitting, isValid, values } = useFormikContext<FormValuesLegalDocs>()
  const { signupData } = useSignup()

  return (
    <div className={styles.button}>
      <Button.Primary size='lg' type='submit' fullWidth disabled={!isValid} loading={isSubmitting}>
        {isLeadTypeIndividual(values.lead ?? signupData.lead)
          ? t('Sign up.Create Profile')
          : isLeadTypeCorporate(values.lead ?? signupData.lead)
          ? t('Next')
          : ''}
      </Button.Primary>
    </div>
  )
}

type GroupReference = string | null

interface OuterProps {
  lead?: LeadDto
  groupReferences: GroupReference[]
  documents: LegalDocumentDto[]
  products: TickmillProductType[]
  isLoading: boolean
  marketDataClassificationId: number
  shouldReceiveNewsletter?: boolean
  onSubmit(v: FormValuesLegalDocs): Promise<void>

  reversed?: boolean
}

export const PersonalDetailsStep6Form = withFormik<OuterProps, FormValuesLegalDocs>({
  mapPropsToValues: (props) => {
    const { lead, shouldReceiveNewsletter } = props

    return {
      lead,
      shouldReceiveNewsletter: shouldReceiveNewsletter || false,
      agreedLegalDocuments: [],
      readDocuments: [],
      readDocumentMarketData: {
        marketDataClassificationId: props.marketDataClassificationId || 0,
        isDocumentMarketDataRead: false,
        isDocumentMarketDataAccepted: props.marketDataClassificationId >= 1 || false,
      },
      documents: {},
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const { onSubmit } = props

    try {
      setSubmitting(true)
      await onSubmit(values)
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { documents }) => {
    const errors: FormikErrors<FormValuesLegalDocs> = {}

    if (!isDocumentsPositiveValue(values.documents)) {
      errors.agreedLegalDocuments = t('Validation.Required')
    }

    if (!isDocumentsAgreed(documents, values.agreedLegalDocuments)) {
      errors.agreedLegalDocuments = t('Validation.Required')
    }

    return errors
  },
  validateOnMount: true,
  enableReinitialize: false,
  isInitialValid: false,
})(PersonalDetailsStep6FormUI)

const isDocumentsPositiveValue = (documents: { [key: string]: boolean } = {}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.entries(documents || []).every(([_, value]) => value)
}

const isDocumentsAgreed = (
  documents: LegalDocumentDto[] = [],
  agreedLegalDocuments: AgreedLegalDocument[] = []
) => {
  return documents.every((document) =>
    agreedLegalDocuments.some((agreedDocument) => agreedDocument.id === document.id)
  )
}
